import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Flex, Typography } from "antd";
import { useMemo } from "react";
import {
	useOrgUsers,
	useWorkspaceMembers,
} from "@/common/artemis-operations/members";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import type { Workspace } from "@/common/model";
import { toLabel } from "@/common/utils";
import { CustomEmpty, IdField } from "@/components/base";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
} from "@/components/responsive-table";

type WorkspaceRow = NonNullable<Workspace>;

// Admins cell for the workspace table — fetches members per row.
function WorkspaceAdminsCell({
	workspaceId,
	userMap,
}: {
	workspaceId: string;
	userMap: Map<string, string>;
}) {
	const { data: members, isPending } = useWorkspaceMembers(workspaceId);
	const admins = (members ?? []).filter((m) => m.role === "WORKSPACE_ADMIN");

	if (isPending)
		return <Typography.Text type="secondary">Loading...</Typography.Text>;
	if (admins.length === 0) return <CustomEmpty description="admins" />;

	return (
		<Typography.Text>
			{admins.map((m) => userMap.get(m.userId) ?? m.userId).join(", ")}
		</Typography.Text>
	);
}

type WorkspaceTableProps = Omit<
	ResponsiveTableProps<WorkspaceRow>,
	"columns" | "compactRender"
>;

/**
 * Workspaces with their id, name, and workspace admins. Compact rows open the workspace. Every other
 * `ResponsiveTable` prop (data, rowActions, pageSize, …) passes through.
 */
export function WorkspaceTable(props: WorkspaceTableProps) {
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const orgUsers = useOrgUsers();

	const userMap = useMemo(() => {
		const map = new Map<string, string>();
		for (const u of orgUsers.data ?? [])
			map.set(u.id, u.displayName || u.email || u.id);
		return map;
	}, [orgUsers.data]);

	const columns: ColumnDef<WorkspaceRow>[] = [
		{
			id: "id",
			header: "Id",
			size: 190,
			cell: ({ row }) => <IdField id={row.original.id} />,
		},
		{
			accessorKey: "name",
			header: "Name",
			size: 250,
			cell: ({ row }) => (
				<IdField
					id={row.original.name}
					link={`/$orgSlug/workspaces/${row.original.id}`}
				/>
			),
		},
		{
			id: "admins",
			header: "Admins",
			cell: ({ row }) => (
				<WorkspaceAdminsCell workspaceId={row.original.id} userMap={userMap} />
			),
		},
	];

	return (
		<ResponsiveTable<WorkspaceRow>
			{...props}
			columns={columns}
			compactRender={(ws) => (
				<Flex
					justify="space-between"
					onClick={() =>
						orgSlug &&
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId",
							params: { orgSlug, workspaceId: ws.id },
						})
					}
					style={{ width: "100%", cursor: "pointer" }}
				>
					<Typography.Text strong ellipsis>
						{toLabel(ws.name)}
					</Typography.Text>
				</Flex>
			)}
		/>
	);
}
